<template>
    <div class="ppv2-space-y-4">
        <!-- Stripe Activation block (Visible in Test mode by default, hidden once keys are saved) -->
        <div v-if="showStripeActivationBlock" class="stripe-activation-block bg-purple ppv2-bg-opacity-10">
            <h3 class="activation-title text-purple poppinsSemiBold">ACTIVER LES PAIEMENTS VIA STRIPE</h3>
            <p class="activation-subtitle ppv2-text-sm ppv2-mb-3">
                Créez une clé d’API Stripe et collez-la ci-dessous pour autoriser les paiements en toute sécurité.
            </p>
            <div class="activation-actions">
                <a
                    href="https://dashboard.stripe.com/apikeys"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="stripe-connect-btn ppv2-text-base bg-purple ppv2-bg-opacity-90 hover:ppv2-bg-opacity-100 transition-all"
                >
                    <svg class="stripe-logo-icon" viewBox="0 0 40 40" fill="#7d2dbd">
                        <path
                            d="M19.1 12.9c-2.9-1.2-4.8-1.9-4.8-3.4 0-1.3 1.2-2.2 3.1-2.2 2.5 0 5 .9 7 2.1l1.3-4.8C23.5 3.3 20.6 2.5 17.5 2.5 11.9 2.5 8.1 5.4 8.1 10.4c0 5.3 4.4 7 8.3 8.6 3.3 1.3 4.9 2.1 4.9 3.7 0 1.5-1.5 2.5-3.8 2.5-3 0-5.9-1.3-8.2-2.7l-1.4 5.1c2.6 1.5 6.1 2.3 9.5 2.3 5.9 0 9.9-2.9 9.9-8.1.1-5.2-4.2-7-8.2-8.9z"
                        />
                    </svg>
                    Se connecter à Stripe
                </a>
                <a
                    href="https://dashboard.stripe.com/register?redirect=%2Fapikeys"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="stripe-register-link text-purple underline poppinsMedium"
                >
                    Je n'ai pas encore de compte Stripe →
                </a>
            </div>
        </div>

        <div class="ppv2-flex ppv2-flex-col ppv2-space-y-4">
            <v-app class="ppv2-relative ppv2-cursor-pointer">
                <SelectableList
                    v-model="formData.stripe_mode"
                    :label="$t('configuration.stripeSetup.fields.stripeMode.label')"
                    :description="$t('configuration.stripeSetup.fields.stripeMode.description')"
                    class="ppv2-mt-4"
                    :options="modeOptions"
                    :inSameLine="true"
                    :hasHalfWidth="true"
                    :withoutBorder="false"
                    customClassItem="!ppv2-py-2"
                    customLabelClass="poppinsSemiBold"
                />

                <template v-if="formData.stripe_mode === 'production'">
                    <BaseInput
                        v-model="formData.stripe_key"
                        :label="$t('configuration.stripeSetup.fields.stripeKey.label')"
                        class="ppv2-mt-4"
                        :placeholder="$t('configuration.stripeSetup.fields.stripeKey.placeholder')"
                        autocomplete="off"
                        required
                        :error="validationErrors.stripe_key"
                        @input="validateField('stripe_key')"
                    >
                        <template #leftIcon>
                            <svg
                                class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                />
                            </svg>
                        </template>
                    </BaseInput>

                    <BaseInput
                        v-model="formData.stripe_secret"
                        type="password"
                        :label="$t('configuration.stripeSetup.fields.stripeSecret.label')"
                        class="ppv2-mt-4"
                        :placeholder="$t('configuration.stripeSetup.fields.stripeSecret.placeholder')"
                        autocomplete="off"
                        required
                        :error="validationErrors.stripe_secret"
                        :helpText="$t('configuration.stripeSetup.fields.stripeSecret.helper')"
                        @input="validateField('stripe_secret')"
                    >
                        <template #leftIcon>
                            <svg
                                class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                />
                            </svg>
                        </template>
                    </BaseInput>
                </template>

                <template v-if="formData.stripe_mode === 'sandbox'">
                    <BaseInput
                        v-model="formData.stripe_sandbox_key"
                        :label="$t('configuration.stripeSetup.fields.stripeKey.label')"
                        class="ppv2-mt-4"
                        :placeholder="$t('configuration.stripeSetup.fields.stripeKey.placeholder')"
                        autocomplete="off"
                        required
                        :error="validationErrors.stripe_sandbox_key"
                        @input="validateField('stripe_sandbox_key')"
                    >
                        <template #leftIcon>
                            <svg
                                class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                />
                            </svg>
                        </template>
                    </BaseInput>

                    <BaseInput
                        v-model="formData.stripe_sandbox_secret"
                        type="password"
                        :label="$t('configuration.stripeSetup.fields.stripeSecret.label')"
                        class="ppv2-mt-4"
                        :placeholder="$t('configuration.stripeSetup.fields.stripeSecret.placeholder')"
                        autocomplete="off"
                        required
                        :error="validationErrors.stripe_sandbox_secret"
                        :helpText="$t('configuration.stripeSetup.fields.stripeSecret.helper')"
                        @input="validateField('stripe_sandbox_secret')"
                    >
                        <template #leftIcon>
                            <svg
                                class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                />
                            </svg>
                        </template>
                    </BaseInput>
                </template>
            </v-app>
        </div>
    </div>
</template>

<script>
import SelectableList from '../../../Shared/SelectableList.vue'
import BaseLabel from '../../../Shared/BaseLabel.vue'
import BaseInput from '../../../Shared/BaseInput.vue'

export default {
    name: 'StripeSetup',
    props: {
        serverIp: {
            type: String,
            default: '',
        },
    },
    components: {
        SelectableList,
        BaseLabel,
        BaseInput,
    },
    data() {
        return {
            customIconLink: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 3H21M21 3V9M21 3L10 14M18 13V19C18 19.5304 17.7893 20.0391 17.4142 20.4142C17.0391 20.7893 16.5304 21 16 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V8C3 7.46957 3.21071 6.96086 3.58579 6.58579C3.96086 6.21071 4.46957 6 5 6H11" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>`,
            isActive: false,
            hasError: false,
            isInitializing: true,
            hasSavedKeysOnLoad: false,
            formData: {
                stripe_mode: null,
                stripe_key: null,
                stripe_secret: null,
                stripe_sandbox_key: null,
                stripe_sandbox_secret: null,
            },
            validationErrors: {
                stripe_key: null,
                stripe_secret: null,
                stripe_sandbox_key: null,
                stripe_sandbox_secret: null,
            },
        }
    },
    computed: {
        appSetting() {
            return this.$store.getters['establishment/appSetting']
        },
        modeOptions() {
            return [
                {
                    label: this.$t('configuration.stripeSetup.fields.stripeMode.options.production.label'),
                    subtitle: this.$t('configuration.stripeSetup.fields.stripeMode.options.production.description'),
                    value: 'production',
                },
                {
                    label: this.$t('configuration.stripeSetup.fields.stripeMode.options.sandbox.label'),
                    subtitle: this.$t('configuration.stripeSetup.fields.stripeMode.options.sandbox.description'),
                    value: 'sandbox',
                },
            ]
        },
        showStripeActivationBlock() {
            return this.formData.stripe_mode === 'sandbox' && !this.hasSavedKeysOnLoad
        },
    },
    watch: {
        formData: {
            deep: true,
            handler(newVal) {
                if (this.isInitializing) return
                const updatedAppSettings = Object.entries(newVal).map(([key, value]) => ({ key, value }))
                this.$store.commit('establishment/ADD_APP_SETTING', updatedAppSettings)
                this.validateAllFields()
            },
        },
    },
    methods: {
        autoFillFromWebsiteSetting() {
            if (!this.appSetting || !Array.isArray(this.appSetting)) return

            this.appSetting.forEach((item) => {
                if (item.key in this.formData) {
                    const currentValue = this.formData[item.key]
                    if (typeof currentValue === 'boolean') {
                        this.formData[item.key] = item.value == 1 || item.value === true || item.value === '1'
                    } else if (typeof currentValue === 'number') {
                        this.formData[item.key] = Number(item.value)
                    } else {
                        this.formData[item.key] = item.value
                    }
                }
            })
        },
        validateField(field) {
            const value = this.formData[field]
            if (!value) {
                this.validationErrors[field] = null
                this.updateHasError()
                return
            }

            let isValid = true
            let errorMessage = ''

            // Regex patterns
            const pkLivePattern = /^pk_live_/
            const skLivePattern = /^sk_live_/
            const pkTestPattern = /^pk_test_/
            const skTestPattern = /^sk_test_/

            if (this.formData.stripe_mode === 'production') {
                if (field === 'stripe_key') {
                    if (!pkLivePattern.test(value)) {
                        isValid = false
                        errorMessage = this.$t('configuration.stripeSetup.errors.invalidLivePublishableKey')
                    }
                } else if (field === 'stripe_secret') {
                    if (!skLivePattern.test(value)) {
                        isValid = false
                        errorMessage = this.$t('configuration.stripeSetup.errors.invalidLiveSecretKey')
                    }
                }
            } else if (this.formData.stripe_mode === 'sandbox') {
                if (field === 'stripe_sandbox_key') {
                    if (!pkTestPattern.test(value)) {
                        isValid = false
                        errorMessage = this.$t('configuration.stripeSetup.errors.invalidTestPublishableKey')
                    }
                } else if (field === 'stripe_sandbox_secret') {
                    if (!skTestPattern.test(value)) {
                        isValid = false
                        errorMessage = this.$t('configuration.stripeSetup.errors.invalidTestSecretKey')
                    }
                }
            }

            this.validationErrors[field] = isValid ? null : errorMessage
            this.updateHasError()
        },
        validateAllFields() {
            if (this.formData.stripe_mode === 'production') {
                this.validateField('stripe_key')
                this.validateField('stripe_secret')
                this.validationErrors.stripe_sandbox_key = null
                this.validationErrors.stripe_sandbox_secret = null
            } else if (this.formData.stripe_mode === 'sandbox') {
                this.validateField('stripe_sandbox_key')
                this.validateField('stripe_sandbox_secret')
                this.validationErrors.stripe_key = null
                this.validationErrors.stripe_secret = null
            }
        },
        updateHasError() {
            this.hasError = Object.values(this.validationErrors).some((error) => error !== null)
            this.$emit('is-valid', !this.hasError)
        },
    },
    created() {
        this.autoFillFromWebsiteSetting()
        if (!this.formData.stripe_mode) {
            this.formData.stripe_mode = 'sandbox'
        }
        // Check if there are keys saved on load
        this.hasSavedKeysOnLoad = !!(
            this.formData.stripe_sandbox_key ||
            this.formData.stripe_sandbox_secret ||
            this.formData.stripe_key ||
            this.formData.stripe_secret
        )
        this.$nextTick(() => {
            this.isInitializing = false
        })
        this.validateAllFields()
    },
}
</script>

<style scoped>
.stripe-activation-block {
    border: 1px solid #dbc1f0;
    border-radius: 12px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    margin-bottom: 24px;
}

.activation-title {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.78px;
    margin-bottom: 8px;
    text-transform: uppercase;
}

.activation-subtitle {
    margin-bottom: 20px;
    max-width: 480px;
}

.activation-actions {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
}

.stripe-connect-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #ffffff !important;
    padding: 10px 24px;
    border-radius: 8px;
    text-decoration: none;
    transition:
        background-color 0.2s ease,
        transform 0.1s ease;
    box-shadow: 0 2px 4px rgba(99, 91, 255, 0.2);
}

.stripe-connect-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(10, 37, 64, 0.2);
}

.stripe-connect-btn:active {
    transform: translateY(0);
}

.stripe-logo-icon {
    width: 21px;
    height: 21px;
    fill: #7d2dbd;
    background-color: currentColor !important;
    border-radius: 6px;
    padding: 3px;
}

.stripe-register-link {
    font-size: 13px;
    transition: color 0.2s ease;
}

.stripe-register-link:hover {
    text-decoration: underline;
}
</style>
